// ===========================================
// COMPONENTE: Item de Notificacion
// ===========================================

import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Calendar,
  CheckSquare,
  FileText,
  Users,
  Settings,
  AlertTriangle,
  X,
  Check,
} from 'lucide-react'
import type { Notificacion, TipoNotificacion } from '@/core/types/notificaciones'
import { TIPO_NOTIFICACION_CONFIG, tiempoRelativo } from '@/core/types/notificaciones'
import { normalizarEnlaceNotificacion } from './enlace-notificacion'

interface NotificacionItemProps {
  notificacion: Notificacion
  onMarcarLeida?: (id: string) => void
  onEliminar?: (id: string) => void
  compact?: boolean
}

// Iconos por tipo
const ICONOS: Record<TipoNotificacion, React.ComponentType<{ className?: string }>> = {
  vencimiento: Calendar,
  tarea: CheckSquare,
  documento: FileText,
  cliente: Users,
  sistema: Settings,
  alerta: AlertTriangle,
}

export function NotificacionItem({
  notificacion,
  onMarcarLeida,
  onEliminar,
  compact = false,
}: NotificacionItemProps) {
  const navigate = useNavigate()
  const config = TIPO_NOTIFICACION_CONFIG[notificacion.tipo]
  const Icon = ICONOS[notificacion.tipo]

  const handleClick = () => {
    // Marcar como leida
    if (!notificacion.leida && onMarcarLeida) {
      onMarcarLeida(notificacion.id)
    }

    // Navegar si hay enlace
    if (notificacion.enlace) {
      navigate(normalizarEnlaceNotificacion(notificacion.enlace))
    }
  }

  if (compact) {
    return (
      <div
        className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
          !notificacion.leida ? 'bg-muted/30' : ''
        }`}
        onClick={handleClick}
      >
        <div
          className={`p-2 rounded-full ${config.bgClass}`}
          style={{ backgroundColor: `${config.color}15` }}
        >
          <Icon className={`h-4 w-4 ${config.color}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium truncate ${!notificacion.leida ? '' : 'text-muted-foreground'}`}>
              {notificacion.titulo}
            </p>
            {!notificacion.leida && (
              <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {notificacion.mensaje}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {tiempoRelativo(notificacion.created_at)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-lg border ${
        !notificacion.leida ? 'bg-muted/30 border-primary/20' : 'bg-background'
      }`}
    >
      <div
        className={`p-2 rounded-full`}
        style={{ backgroundColor: `${config.color}15` }}
      >
        <Icon className={`h-5 w-5 ${config.color}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline" className={config.bgClass}>
            {config.label}
          </Badge>
          {!notificacion.leida && (
            <Badge variant="default" className="bg-blue-500">
              Nueva
            </Badge>
          )}
        </div>

        <h4 className="font-medium">{notificacion.titulo}</h4>
        <p className="text-sm text-muted-foreground mt-1">
          {notificacion.mensaje}
        </p>

        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-muted-foreground">
            {tiempoRelativo(notificacion.created_at)}
          </span>

          <div className="flex gap-1">
            {!notificacion.leida && onMarcarLeida && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onMarcarLeida(notificacion.id)
                }}
              >
                <Check className="h-4 w-4 mr-1" />
                Marcar leida
              </Button>
            )}

            {notificacion.enlace && (
              <Button variant="outline" size="sm" onClick={handleClick}>
                Ver detalle
              </Button>
            )}

            {onEliminar && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onEliminar(notificacion.id)
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
